const STYLE_ID = 'ap-bottom-sheet-styles';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
.ap-sheet-overlay{position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,.34);opacity:0;pointer-events:none;transition:opacity .22s ease;touch-action:none}
.ap-sheet-overlay.is-open{opacity:1;pointer-events:auto}
.ap-sheet{position:fixed;left:0;right:0;bottom:0;z-index:10001;min-height:160px;max-height:92vh;background:#fff;color:#111;border-radius:24px 24px 0 0;box-shadow:0 -18px 60px rgba(0,0,0,.28);transform:translate3d(0,100%,0);transition:transform .28s cubic-bezier(.22,1,.36,1);will-change:transform;touch-action:none;overflow:hidden}
.ap-sheet.is-open{transform:translate3d(0,0,0)}
.ap-sheet.is-dragging{transition:none}
.ap-sheet__handle{width:44px;height:5px;border-radius:999px;background:#d8d8d8;margin:10px auto 8px}
.ap-sheet__header{display:flex;align-items:center;gap:12px;padding:0 18px 10px}
.ap-sheet__title{font:600 18px/1.2 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;margin:0;flex:1}
.ap-sheet__close{border:0;background:#f1f1f1;border-radius:999px;width:34px;height:34px;font-size:22px;line-height:1;cursor:pointer}
.ap-sheet__content{max-height:calc(92vh - 68px);overflow:auto;-webkit-overflow-scrolling:touch;padding:0 18px calc(18px + env(safe-area-inset-bottom))}
@media (prefers-color-scheme:dark){.ap-sheet{background:#111;color:#f5f5f5}.ap-sheet__close{background:#252525;color:#fff}.ap-sheet__handle{background:#444}}
@media (prefers-reduced-motion:reduce){.ap-sheet,.ap-sheet-overlay{transition:none}}
`;
  document.head.appendChild(style);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function createBottomSheet(options = {}) {
  injectStyles();

  const overlay = document.createElement('div');
  overlay.className = 'ap-sheet-overlay';

  const sheet = document.createElement('section');
  sheet.className = 'ap-sheet';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-hidden', 'true');

  sheet.innerHTML = `
    <div class="ap-sheet__handle" data-ap-sheet-drag></div>
    <div class="ap-sheet__header" data-ap-sheet-drag>
      <h2 class="ap-sheet__title"></h2>
      <button class="ap-sheet__close" type="button" aria-label="Close">×</button>
    </div>
    <div class="ap-sheet__content"></div>
  `;

  const titleEl = sheet.querySelector('.ap-sheet__title');
  const contentEl = sheet.querySelector('.ap-sheet__content');
  const closeBtn = sheet.querySelector('.ap-sheet__close');
  const dragTargets = sheet.querySelectorAll('[data-ap-sheet-drag]');

  let isOpen = false;
  let startY = 0;
  let currentY = 0;
  let startTime = 0;

  function setTranslate(y) {
    sheet.style.transform = `translate3d(0,${Math.max(0, y)}px,0)`;
  }

  function setContent(content) {
    if (content instanceof Node) {
      contentEl.replaceChildren(content);
    } else {
      contentEl.innerHTML = content == null ? '' : String(content);
    }
  }

  function open(params = {}) {
    if (params.title !== undefined) titleEl.textContent = params.title;
    if (params.content !== undefined) setContent(params.content);

    if (!overlay.isConnected) document.body.appendChild(overlay);
    if (!sheet.isConnected) document.body.appendChild(sheet);

    requestAnimationFrame(() => {
      overlay.classList.add('is-open');
      sheet.classList.add('is-open');
      sheet.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      isOpen = true;
    });
  }

  function close() {
    overlay.classList.remove('is-open');
    sheet.classList.remove('is-open', 'is-dragging');
    sheet.setAttribute('aria-hidden', 'true');
    sheet.style.transform = '';
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    isOpen = false;
  }

  function destroy() {
    close();
    overlay.remove();
    sheet.remove();
  }

  function onPointerDown(event) {
    if (!isOpen) return;

    startY = event.clientY;
    currentY = startY;
    startTime = performance.now();
    sheet.classList.add('is-dragging');
    sheet.setPointerCapture?.(event.pointerId);
  }

  function onPointerMove(event) {
    if (!sheet.classList.contains('is-dragging')) return;

    currentY = event.clientY;
    setTranslate(clamp(currentY - startY, 0, window.innerHeight));
  }

  function onPointerUp(event) {
    if (!sheet.classList.contains('is-dragging')) return;

    const deltaY = currentY - startY;
    const elapsed = Math.max(1, performance.now() - startTime);
    const velocity = deltaY / elapsed;
    const shouldClose = deltaY > 110 || velocity > 0.65;

    sheet.classList.remove('is-dragging');
    sheet.releasePointerCapture?.(event.pointerId);

    if (shouldClose) {
      close();
    } else {
      setTranslate(0);
      requestAnimationFrame(() => {
        sheet.style.transform = '';
      });
    }
  }

  overlay.addEventListener('click', close);
  closeBtn.addEventListener('click', close);
  dragTargets.forEach((target) => {
    target.addEventListener('pointerdown', onPointerDown);
  });
  sheet.addEventListener('pointermove', onPointerMove);
  sheet.addEventListener('pointerup', onPointerUp);
  sheet.addEventListener('pointercancel', onPointerUp);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen) close();
  });

  if (options.title) titleEl.textContent = options.title;
  if (options.content) setContent(options.content);

  return {
    open,
    close,
    destroy,
    setContent,
    get element() {
      return sheet;
    },
  };
}
